import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { Bell, X, LucideIconData } from 'lucide-angular';

import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../button/button.component';
import { InputComponent } from '../input/input.component';
import { AuthService } from '../../services/auth.service';
import { StockAlertModalService } from '../../services/stock-alert-modal.service';
import { StockAlertStore } from '../../services/stock-alert.store';
import { ToastService } from '../../services/toast.service';
import { StockAlertError } from '../../models/stock-alert.model';

const FALLBACK_IMAGE_URL = '/images/hero/broken_hero.jpg';

type NotifyStatus =
  | 'idle'
  | 'pending'
  | 'success'
  | 'already_subscribed'
  | 'prepurchase'
  | 'error';

@Component({
  selector: 'app-stock-alert-modal',
  imports: [IconComponent, ButtonComponent, InputComponent],
  templateUrl: './stock-alert-modal.component.html',
  styleUrl: './stock-alert-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StockAlertModalComponent {
  private readonly modalService = inject(StockAlertModalService);
  private readonly stockAlertStore = inject(StockAlertStore);
  private readonly toastService = inject(ToastService);
  private readonly authService = inject(AuthService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isOpen = this.modalService.isOpen;
  protected readonly product = this.modalService.product;

  protected readonly bellIcon: LucideIconData = Bell;
  protected readonly closeIcon: LucideIconData = X;

  protected readonly status = signal<NotifyStatus>('idle');
  protected readonly email = signal('');

  private readonly closeButtonRef = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');
  private readonly inputWrapperRef = viewChild<ElementRef<HTMLElement>>('inputWrap');
  private readonly modalRef = viewChild<ElementRef<HTMLElement>>('modalEl');
  private previouslyFocusedElement: HTMLElement | null = null;

  protected readonly thumbnailUrl = computed(
    () => this.product()?.imageUrl || FALLBACK_IMAGE_URL,
  );

  protected readonly subscriberCount = computed(
    () => this.product()?.stock_alert?.subscriber_count ?? 0,
  );

  /**
   * «Enviando»: el alta la lanzó este modal o hay otra del mismo producto en
   * vuelo (p. ej. desde la ficha). El candado es de `StockAlertModalService`.
   */
  protected readonly pending = computed(
    () => this.status() === 'pending' || this.modalService.isSubscribing(this.product()?.id),
  );

  /**
   * El store ya tiene el token de este producto (p. ej. terminó el alta que
   * estaba en vuelo desde la ficha): el modal lo confirma en vez de ofrecer
   * otra alta.
   */
  protected readonly subscribed = computed(
    () => this.status() === 'success' || this.stockAlertStore.hasAlert(this.product()?.id),
  );

  /**
   * Secuencia de altas del modal. El modal es global y el servicio lo cierra en
   * cada `NavigationEnd` aunque haya un alta en vuelo: su respuesta puede
   * llegar con el modal reabierto para otro producto. `resetState()` (cada
   * apertura) la incrementa; un callback solo toca el modal si sigue vigente.
   */
  private opSeq = 0;

  /** El submit es válido si no hay alta en vuelo y el email tiene forma de email. */
  protected readonly canSubmit = computed(() => {
    if (this.pending()) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email().trim());
  });

  constructor() {
    // Reset + focus al abrir; restore focus al cerrar.
    effect(() => {
      const open = this.isOpen();
      const product = this.product();
      if (!isPlatformBrowser(this.platformId)) return;

      if (open && product) {
        this.resetState();
        this.previouslyFocusedElement =
          (document.activeElement as HTMLElement | null) ?? null;
        // Foco al input del email (no al botón cerrar — el usuario va a teclear).
        queueMicrotask(() => {
          const input = this.inputWrapperRef()?.nativeElement.querySelector('input');
          (input as HTMLInputElement | null)?.focus();
        });
      } else if (!open && this.previouslyFocusedElement) {
        const el = this.previouslyFocusedElement;
        this.previouslyFocusedElement = null;
        queueMicrotask(() => {
          if (document.contains(el)) {
            el.focus({ preventScroll: true });
          }
        });
      }
    });

    // ESC cierra.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key === 'Escape' && this.isOpen()) {
            this.onClose();
          }
        });
    });

    // Focus trap: Tab/Shift+Tab circulan solo dentro del modal.
    afterNextRender(() => {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((e) => {
          if (e.key !== 'Tab' || !this.isOpen()) return;

          const modal = this.modalRef()?.nativeElement;
          if (!modal) return;

          const focusables = this.getFocusableElements(modal);
          if (focusables.length === 0) return;

          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;

          if (!active || !modal.contains(active)) {
            e.preventDefault();
            first.focus();
            return;
          }

          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        });
    });
  }

  protected onClose(): void {
    if (this.pending()) return;
    this.modalService.close();
  }

  protected onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  protected onSubmit(event?: Event): void {
    // Prevenimos el submit nativo del <form> — sin esto el browser navega
    // a la URL actual ("/?") porque no hay FormsModule importado para
    // interceptar via (ngSubmit).
    event?.preventDefault();
    const product = this.product();
    if (!product) return;
    if (!this.canSubmit()) return;

    const productId = Number(product.id);
    if (!Number.isFinite(productId)) return;

    const email = this.email().trim();
    this.status.set('pending');
    const op = ++this.opSeq;
    const isCurrent = () => op === this.opSeq && Number(this.product()?.id) === productId;

    // Mismo POST que la ficha (`requestAlert`): un alta en vuelo por producto y
    // el token al store siempre. Aquí solo quedan los efectos del modal.
    const started = this.modalService.requestAlert(productId, email, {
      onSuccess: () => {
        if (!isCurrent()) return;
        this.status.set('success');
        this.toastService.show({
          type: 'success',
          message: 'Te avisaremos cuando esté disponible.',
        });
        // Cierre breve para que el usuario vea la confirmación inline.
        setTimeout(() => {
          if (isCurrent() && this.status() === 'success') this.modalService.close();
        }, 700);
      },
      onError: (err) => {
        if (!isCurrent()) return;
        if (err instanceof StockAlertError) {
          if (err.reason === 'already_subscribed') {
            this.status.set('already_subscribed');
            return;
          }
          if (err.reason === 'product_is_prepurchase') {
            this.status.set('prepurchase');
            return;
          }
        }
        this.status.set('error');
      },
    });
    // Otra alta del mismo producto ya estaba en vuelo: no sale POST y el modal
    // sigue en «enviando» vía `pending` hasta que termine.
    if (!started) this.status.set('idle');
  }

  private resetState(): void {
    this.opSeq++;
    this.status.set('idle');
    const prefill = this.authService.currentUser()?.email ?? '';
    this.email.set(prefill);
  }

  /** Devuelve los elementos enfocables dentro del modal en orden DOM. */
  private getFocusableElements(root: HTMLElement): HTMLElement[] {
    const selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selector)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
  }

  protected onImageError(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img && !img.src.endsWith(FALLBACK_IMAGE_URL)) {
      img.src = FALLBACK_IMAGE_URL;
    }
  }
}
